import { useEffect, type ComponentProps, type FunctionComponent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { trackPageLoad } from '@fff-web/fff-analytics';
import AccountDetailsPage from '../AccountDetails/AccountDetailsPage';
import InvoiceHistory from '../InvoiceHistory/InvoiceHistory';
import { SubscriptionStatus } from '../MyAccountPage';
import Orders from '../Orders';
import PersonalSurvey from '../PersonalSurvey/PersonalSurvey';
import RateAndReview from '../RateAndReview/RateAndReview';

type AccountDetailsPageProps = ComponentProps<typeof AccountDetailsPage> & {
  isDesktop: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
  boxSubscriptionId?: string | null;
};

const MyAccountRoutes: FunctionComponent<AccountDetailsPageProps> = ({
  firstName,
  lastName,
  shippingAddress,
  phone,
  email,
  credits,
  billingInfo,
  setBillingInfo,
  isDesktop,
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
  boxSubscriptionId,
}) => {
  const { pathname } = useLocation();
  const { myAccountOrderHistory: myAccountOrderHistoryFF, rateAndReview: rateAndReviewFF } =
    useFlags();
  const navigate = useNavigate();

  // redirect user to /my-account/invoice-history if myAccountOrderHistory FF is OFF and access /my-account/orders
  useEffect(() => {
    const isOrdersPath = pathname.includes('orders');
    if (isOrdersPath && !myAccountOrderHistoryFF) {
      navigate('/my-account/invoice-history');
    }
  }, [myAccountOrderHistoryFF, navigate, pathname]);

  useEffect(() => {
    trackPageLoad({ pathName: pathname });
  }, [pathname]);

  return (
    <Routes>
      <Route
        index
        element={
          <AccountDetailsPage
            firstName={firstName}
            lastName={lastName}
            phone={phone}
            email={email}
            shippingAddress={shippingAddress}
            credits={credits}
            billingInfo={billingInfo}
            setBillingInfo={setBillingInfo}
            isClubMember={isClubMember}
            clubMemberSubscriptionId={clubMemberSubscriptionId}
            subscriptionState={subscriptionState}
            refetchClubMemberSubscription={refetchClubMemberSubscription}
            boxSubscriptionId={boxSubscriptionId}
          />
        }
      />
      <Route path="orders" element={<Orders isDesktop={isDesktop} />} />
      {rateAndReviewFF && <Route path="rate-and-review" element={<RateAndReview />} />}
      <Route path="invoice-history" element={<InvoiceHistory />} />
      <Route path="personal-survey" element={<PersonalSurvey />} />
    </Routes>
  );
};

export default MyAccountRoutes;
